import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 24; // ~60s of polling

/**
 * Reached via Stripe success redirect: /app/planos/sucesso?session_id=...
 * Polls /api/payments/status until backend confirms the payment landed and
 * credited the user (idempotent).
 */
export default function PricingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payload: null });
  const attemptsRef = useRef(0);
  const { refresh } = useAuth();

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ status: "missing_session", payload: null });
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid" || data.credited) {
        setState({ status: "paid", payload: data });
        refresh?.();
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", payload: data });
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState({ status: "timeout", payload: data });
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setState({ status: "error", payload: null });
    }
  }, [sessionId, refresh]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="p-8 lg:p-12 max-w-2xl mx-auto" data-testid="pricing-success-page">
      <div className="card text-center py-12">
        {state.status === "loading" && (
          <>
            <i className="fa-solid fa-circle-notch fa-spin text-5xl text-emerald-900 mb-4"></i>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Confirmando seu pagamento…</h1>
            <p className="text-slate-600 mt-3">
              Isso leva alguns segundos. Não feche essa página.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto bg-emerald-900 rounded-full flex items-center justify-center mb-4">
              <i className="fa-solid fa-check text-white text-2xl"></i>
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="pricing-success-title">
              Pagamento aprovado!
            </h1>
            <p className="text-slate-600 mt-3">
              Seu plano <strong>{state.payload?.plan_label}</strong> está ativo.
              Agora você pode gerar relatórios AEP em PDF sem restrição.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/app/avaliacoes" className="btn btn-primary" data-testid="pricing-success-go-assessments">
                <i className="fa-solid fa-list-check"></i> Ir para avaliações
              </Link>
              <Link to="/app" className="btn btn-secondary" data-testid="pricing-success-go-dashboard">
                <i className="fa-solid fa-gauge"></i> Voltar ao painel
              </Link>
            </div>
          </>
        )}
        {state.status === "expired" && (
          <>
            <i className="fa-solid fa-circle-xmark text-5xl text-rose-500 mb-4"></i>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Sessão expirada</h1>
            <p className="text-slate-600 mt-3">A sessão de pagamento expirou antes da confirmação.</p>
            <Link to="/app/planos" className="btn btn-primary mt-6">Tentar novamente</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <i className="fa-solid fa-clock text-5xl text-amber-500 mb-4"></i>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Verificação demorando…</h1>
            <p className="text-slate-600 mt-3">
              O Stripe ainda não confirmou. Aguarde alguns minutos — a aprovação chegará no banco e o plano será liberado.
              Se nada acontecer, contate o suporte com este código:{" "}
              <span className="font-mono text-xs">{sessionId}</span>
            </p>
            <Link to="/app" className="btn btn-secondary mt-6">Voltar ao painel</Link>
          </>
        )}
        {(state.status === "missing_session" || state.status === "error") && (
          <>
            <i className="fa-solid fa-circle-exclamation text-5xl text-rose-500 mb-4"></i>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Algo deu errado</h1>
            <p className="text-slate-600 mt-3">Não foi possível verificar o pagamento. Tente novamente.</p>
            <Link to="/app/planos" className="btn btn-primary mt-6">Voltar aos planos</Link>
          </>
        )}
      </div>
    </div>
  );
}
